/**
 * src/services/shareHostStore.js — HOST-side broadcast owner (post-launch fix).
 *
 * Bug this replaces: broadcast lived inside ShareModal, so closing the modal
 * (or a re-render unmount) silently stopped all frame pushes while the share
 * stayed live server-side. Viewers stared at "Waiting for the HOST…" forever
 * and the host had no on/off control — the only lever was revoke.
 *
 * Design: a tiny framework-agnostic singleton OWNS the broadcast loop.
 * ShareModal/AppShell are thin views over it (useSyncExternalStore). The
 * loop (1) re-emits the last mirrored frame on an interval so a hub wiped by
 * a server restart/deploy self-heals, and (2) pushes a fresh navigation frame
 * on route changes, so a host sitting still on any screen still broadcasts.
 *
 * Frames remain a live MIRROR only — this store never grants a Viewer-Guest
 * data access; nothing here reads Dexie/PB content beyond the screen slug.
 *
 * Terminology: HOST / Viewer-Guest — 'Lead' stays reserved for the bot concept.
 */

import { pushFrame } from './shareApi.js';

const KEEPALIVE_MS = 30_000; // re-assert last frame — heals a wiped in-memory hub

let state = {
  shareId: null, // active (or adopted) share session id
  screenSlug: 'headquarters',
  businessName: null,
  paused: false, // HOST's explicit on/off control
  lastDelivered: 0, // viewers reached by the last successful frame
  lastError: null, // 'revoked' | 'expired' | message string | null
  expiresAt: null
};

const listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  for (const fn of listeners) fn();
}

export function subscribeShareHost(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getShareHostState() {
  return state;
}

let keepaliveTimer = null;
let lastFrameRef = null; // { screenSlug, payload } — re-asserted by the keepalive

function isTerminal() {
  return state.lastError === 'revoked' || state.lastError === 'expired';
}

async function sendFrame(screenSlug, payload) {
  if (!state.shareId || state.paused || isTerminal()) return;
  lastFrameRef = { screenSlug, payload };
  try {
    const result = await pushFrame(state.shareId, { screenSlug, payload });
    setState({ lastDelivered: result.delivered ?? 0, lastError: null });
  } catch (err) {
    const code = err?.code;
    if (code === 'SHARE_REVOKED' || code === 'SHARE_EXPIRED') {
      stopKeepalive();
      setState({ lastError: code === 'SHARE_REVOKED' ? 'revoked' : 'expired' });
      return;
    }
    setState({ lastError: err?.message || 'Frame push failed' });
  }
}

function startKeepalive() {
  stopKeepalive();
  keepaliveTimer = setInterval(() => keepaliveTick(), KEEPALIVE_MS);
}

/**
 * One keepalive cycle. Exported so tests (scripts/test-share-e2e.mjs) can
 * exercise the re-assert path without waiting KEEPALIVE_MS in real time.
 */
export function keepaliveTick() {
  if (!state.shareId || state.paused || isTerminal()) return;
  // Re-assert the last mirrored frame: if the server restarted (hub is
  // memory-only) late-joining and reconnected viewers still get a snapshot
  // instead of an empty "Waiting…" screen until the host interacts again.
  if (lastFrameRef) {
    sendFrame(lastFrameRef.screenSlug, lastFrameRef.payload);
    return;
  }
  // Host hasn't mirrored anything yet — push a minimal presence frame so
  // viewers immediately leave "Waiting for the HOST to share their screen…".
  sendFrame(state.screenSlug, {
    kind: 'overview',
    business: state.businessName || state.screenSlug,
    screen: state.screenSlug
  });
}

function stopKeepalive() {
  if (keepaliveTimer) {
    clearInterval(keepaliveTimer);
    keepaliveTimer = null;
  }
}

/** HOST pushes (or refreshes) what viewers currently see. */
export function broadcastFrame(screenSlug, payload) {
  if (!state.shareId) return;
  setState({ screenSlug: screenSlug || state.screenSlug });
  sendFrame(screenSlug || state.screenSlug, payload);
}

/**
 * HOST navigated: mirror the new screen even if nothing on it emits frames.
 * segmentCount === 2 (…/workspace/<company>) resolves to the router's index
 * redirect target, never the company slug itself.
 */
export function mirrorRoute(pathname, businessName = null) {
  if (!state.shareId) return;
  const segments = String(pathname || '').split('/').filter(Boolean);
  const screenSlug = segments.length >= 3 ? segments[segments.length - 1] : 'headquarters';
  setState({
    screenSlug,
    ...(businessName ? { businessName } : {})
  });
  sendFrame(screenSlug, {
    kind: 'overview',
    business: businessName || state.businessName || screenSlug,
    screen: screenSlug
  });
}

/** ShareModal created a share → the loop starts immediately. */
export function activateShare({ shareId, businessName = null, expiresAt = null }) {
  lastFrameRef = null;
  setState({
    shareId,
    businessName,
    expiresAt,
    paused: false,
    lastError: null,
    lastDelivered: 0
  });
  startKeepalive();
  // Immediate presence frame — viewers join a live screen, not "Waiting…".
  sendFrame(state.screenSlug, {
    kind: 'overview',
    business: businessName || state.screenSlug,
    screen: state.screenSlug
  });
}

/**
 * Reload/rejoin: the PB row is still live (e.g. page refresh mid-share).
 * Restart the loop for an existing session without a fresh credential pair.
 */
export function adoptShare(shareId, { businessName = null, expiresAt = null } = {}) {
  if (state.shareId === shareId) return;
  lastFrameRef = null;
  setState({
    shareId,
    businessName,
    expiresAt,
    paused: false,
    lastError: null,
    lastDelivered: 0
  });
  startKeepalive();
}

/** HOST's explicit ON/OFF. Pausing stops frames; viewers are told via keepalive absence. */
export function setSharePaused(paused) {
  if (!state.shareId) return;
  setState({ paused: Boolean(paused) });
  if (!paused) {
    // Resume: re-assert presence immediately rather than waiting a full cycle.
    if (lastFrameRef) sendFrame(lastFrameRef.screenSlug, lastFrameRef.payload);
    else sendFrame(state.screenSlug, {
      kind: 'overview',
      business: state.businessName || state.screenSlug,
      screen: state.screenSlug
    });
  }
}

/** Revoke / expiry / ended — full local teardown. */
export function clearShare() {
  stopKeepalive();
  lastFrameRef = null;
  setState({
    shareId: null,
    paused: false,
    lastDelivered: 0,
    lastError: null,
    expiresAt: null,
    screenSlug: 'headquarters'
  });
}
